/**
 * Static per-drone identity lookup.
 *
 * v1's `networkid/#` payload only carries `UAS_ID`, `callsign`, and
 * `UAS_TYPE`. The v2 UI also wants `drone_model`, `drone_type` (display
 * string), and per-drone DAA radii. Those don't change at telemetry rate,
 * so we look them up by `UAS_ID` from a static table instead of demanding
 * them on every message.
 *
 * Unknown drones (any `UAS_ID` not in the table) get sensible defaults —
 * the UI still renders, just with `Unknown` for model/type.
 */

export interface FleetIdentity {
  callsign: string | null;
  drone_model: string;
  drone_type: string; // VTOL / Multirotor / Fixed-wing / Unknown
  nmacRadius: number; // metres
  swcRadius: number; // metres
  evalRadius: number; // metres
}

const DEFAULT_RADII = {
  nmacRadius: 304.8,
  swcRadius: 1219.2,
  evalRadius: 12_000,
} as const;

/** Map UAS_TYPE codes from v1's networkid payload to display strings.
 *  Per v1's drones.tsx marker logic: 1 = airplane (fixed-wing-ish),
 *  2 = multirotor. Anything else we leave Unknown. */
function droneTypeFromUasType(uasType: number | null | undefined): string {
  if (uasType === 1) return 'Fixed-wing';
  if (uasType === 2) return 'Multirotor';
  return 'Unknown';
}

/** Known fleet — seeded from the v2 mock fleet so the staging drones
 *  (if any of these UAS_IDs ever publish on the live broker) render with
 *  their familiar model/type instead of "Unknown". Expand as needed. */
const KNOWN_IDENTITIES: Record<string, Omit<FleetIdentity, 'callsign'>> = {
  AIRTL69:        { drone_model: 'Sentaeros 6',     drone_type: 'VTOL',       ...DEFAULT_RADII },
  AIRTL11:        { drone_model: 'Phoenix VX-24',   drone_type: 'VTOL',       ...DEFAULT_RADII },
  'SC-Hawk-01':   { drone_model: 'Matrice 4TD',     drone_type: 'Multirotor', ...DEFAULT_RADII },
  'EPS-Eagle-03': { drone_model: 'Matrice 4TD',     drone_type: 'Multirotor', ...DEFAULT_RADII },
};

/**
 * Resolve a `UAS_ID` to its display identity. Falls back to safe defaults
 * for unknown drones so the live mode auto-discovers anything the broker
 * publishes without needing a code change.
 *
 * If `uasType` is provided (from the message itself), we use it to refine
 * the `drone_type` for unknown UAS_IDs.
 */
export function resolveFleetIdentity(uasId: string, uasType?: number | null): FleetIdentity {
  const known = KNOWN_IDENTITIES[uasId];
  if (known) {
    return { callsign: null, ...known };
  }
  return {
    callsign: null,
    drone_model: 'Unknown',
    drone_type: droneTypeFromUasType(uasType),
    ...DEFAULT_RADII,
  };
}
